// components/about/MissionVisionValuesMinimal.tsx

import React from "react";
import { motion } from "framer-motion";
import {
  Target,
  Eye,
  Heart,
  Shield,
  Users,
  Lightbulb,
  Globe,
  Award,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

const MissionVisionValuesMinimal: React.FC = () => {
  const values = [
    { icon: Heart, title: "Human-Centered", color: "#E05A6A" },
    { icon: Shield, title: "Integrity", color: "#8B52FF" },
    { icon: Users, title: "Inclusivity", color: "#A855F7" },
    { icon: Lightbulb, title: "Innovation", color: "#E05A6A" },
    { icon: Globe, title: "Global Impact", color: "#8B52FF" },
    { icon: Award, title: "Excellence", color: "#A855F7" },
  ];

  return (
    <section className="relative py-16 bg-white overflow-hidden">
      {/* Subtle background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#8B52FF]/[0.02] via-transparent to-[#E05A6A]/[0.02]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mission Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-24 lg:mb-32"
        >
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#8B52FF] to-[#A855F7] flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-semibold text-[#8B52FF] uppercase tracking-wider">
                  Our Mission
                </span>
              </div>

              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 leading-tight">
                Empowering people to build{" "}
                <span className="bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] bg-clip-text text-transparent">
                  AI that works for everyone
                </span>
              </h2>
            </div>

            <div>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                We're on a mission to democratize AI development by connecting
                talented individuals worldwide with meaningful opportunities. We
                believe the best AI is built when diverse perspectives come
                together.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Every day, our community of experts helps train, evaluate, and
                improve AI systems ensuring technology serves humanity's best
                interests.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Vision Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="lg:order-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#E05A6A] to-[#C75A70] flex items-center justify-center">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-semibold text-[#E05A6A] uppercase tracking-wider">
                  Our Vision
                </span>
              </div>

              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 leading-tight">
                A future where{" "}
                <span className="bg-gradient-to-r from-[#E05A6A] to-[#8B52FF] bg-clip-text text-transparent">
                  AI understands every voice
                </span>
              </h2>
            </div>

            <div className="lg:order-1">
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                We envision a world where artificial intelligence truly
                understands and respects every culture, language, and human
                perspective.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Through our global community, we're building technology that's
                inclusive by design. AI that serves everyone equally, no matter
                where they come from.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Values Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <span className="text-sm font-semibold text-[#8B52FF] uppercase tracking-wider">
              Our Values
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2">
              What We Stand For
            </h2>
          </div>

          {/* Values as horizontal scroll on mobile, grid on desktop */}
          <div className="flex lg:grid lg:grid-cols-6 gap-6 overflow-x-auto pb-4 lg:pb-0 scrollbar-hide">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="flex-shrink-0 w-40 lg:w-auto"
              >
                <div className="text-center p-6 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-xl transition-all duration-300 h-full">
                  <div
                    className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${value.color}15` }}
                  >
                    <value.icon
                      className="w-7 h-7"
                      style={{ color: value.color }}
                    />
                  </div>
                  <h4 className="font-semibold text-gray-900">{value.title}</h4>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-16 text-center"
          >
            <p className="text-gray-600 mb-6">
              Ready to be part of something meaningful?
            </p>
            <Link to="/careers">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white rounded-xl font-semibold shadow-xl shadow-[#8B52FF]/20 hover:shadow-2xl transition-all duration-300 group"
              >
                Join Our Mission
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Add custom scrollbar hide CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

export default MissionVisionValuesMinimal;
